import { useQuery } from '@apollo/client';
import { isEmpty } from '@newfront-insurance/core';
import { useMemo } from 'react';

import { GET_SLASH_COMMANDS, type SlashCommand } from '../queries/chats';

interface UseSlashCommandsOptions {
  accountUuid?: string;
  userUuid?: string;
}

interface SlashCommandGroup {
  category: string;
  commands: SlashCommand[];
}

export function useSlashCommands({ accountUuid, userUuid }: UseSlashCommandsOptions) {
  const { data, loading, error } = useQuery(GET_SLASH_COMMANDS, {
    variables: {
      accountUuid: accountUuid ?? '',
      userUuid: userUuid ?? '',
    },
    skip: isEmpty(accountUuid) || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const commands = useMemo(() => data?.slashCommands ?? [], [data?.slashCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, SlashCommand[]> = {};

    commands.forEach((command) => {
      const { category } = command;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(command);
    });

    return Object.entries(groups).map(
      ([category, categoryCommands]): SlashCommandGroup => ({
        category,
        commands: categoryCommands,
      }),
    );
  }, [commands]);

  // Filter commands based on search query
  const filterCommands = useMemo(() => {
    return (query: string): SlashCommand[] => {
      if (isEmpty(query)) {
        return commands;
      }

      const normalizedQuery = query.toLowerCase().replace(/^\//, '');
      return commands.filter(
        (command) =>
          command.handle.toLowerCase().includes(normalizedQuery) ||
          command.description.toLowerCase().includes(normalizedQuery),
      );
    };
  }, [commands]);

  return {
    commands,
    groupedCommands,
    filterCommands,
    isLoading: loading,
    error,
  };
}
